import { useState } from 'react';
import { Loader2, AlertTriangle, Clock, CalendarClock, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { useGearForecastReport } from '@/hooks/use-reports';
import type { GearForecastFilters, GearForecastItem } from '@/hooks/use-reports';
import { cn } from '@/app/components/ui/utils';

const BUCKET_CONFIG = [
  { key: 'overdue', label: 'Overdue', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle, iconColor: 'text-red-500' },
  { key: '<=30', label: '0\u201330 Days', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', icon: Clock, iconColor: 'text-orange-500' },
  { key: '<=60', label: '31\u201360 Days', color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: CalendarClock, iconColor: 'text-yellow-500' },
  { key: '<=90', label: '61\u201390 Days', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: CalendarClock, iconColor: 'text-blue-500' },
] as const;

const DUE_TYPE_LABELS: Record<string, string> = {
  inspection: 'Inspection Due',
  service_life: 'Service Life',
};

const PAGE_SIZE = 50;

export function GearForecastReportPage() {
  const [filters, setFilters] = useState<GearForecastFilters>({ page_size: PAGE_SIZE });
  const [searchInput, setSearchInput] = useState('');
  const [activeBucket, setActiveBucket] = useState<string | undefined>();
  const [activeDueType, setActiveDueType] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const currentFilters: GearForecastFilters = {
    ...filters,
    q: filters.q || undefined,
    bucket: activeBucket,
    due_type: activeDueType,
    page,
    page_size: PAGE_SIZE,
  };

  const { data, isLoading, error } = useGearForecastReport(currentFilters);

  const handleSearch = () => {
    setFilters((f) => ({ ...f, q: searchInput }));
    setPage(1);
  };

  const handleBucketClick = (bucket: string) => {
    setActiveBucket((prev) => (prev === bucket ? undefined : bucket));
    setPage(1);
  };

  const handleDueTypeClick = (dueType: string) => {
    setActiveDueType((prev) => (prev === dueType ? undefined : dueType));
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load gear forecast report. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gear Forecast</h1>
        <p className="text-gray-600 mt-1">Equipment expiring or due for inspection in the next 90 days</p>
      </div>

      {/* Bucket summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {BUCKET_CONFIG.map(({ key, label, color, bg: _bg, border, icon: Icon, iconColor }) => {
          const count = data?.counts_by_bucket?.[key] ?? 0;
          const isActive = activeBucket === key;
          return (
            <Card
              key={key}
              className={cn(
                'cursor-pointer transition-all hover:shadow-md',
                isActive ? `${border} ring-2 ring-offset-1 ring-current ${color}` : '',
              )}
              onClick={() => handleBucketClick(key)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{label}</CardTitle>
                <Icon className={cn('h-4 w-4', iconColor)} />
              </CardHeader>
              <CardContent>
                <div className={cn('text-2xl font-bold', color)}>{count}</div>
                <p className="text-xs text-muted-foreground">
                  {count === 1 ? 'item' : 'items'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Due-type toggle + search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2">
          {Object.entries(DUE_TYPE_LABELS).map(([key, label]) => {
            const count = data?.counts_by_due_type?.[key] ?? 0;
            const isActive = activeDueType === key;
            return (
              <Button
                key={key}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleDueTypeClick(key)}
              >
                {label} ({count})
              </Button>
            );
          })}
        </div>
        <div className="flex gap-2 ml-auto">
          <Input
            placeholder="Search serial, manufacturer, model..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="w-64"
          />
          <Button variant="outline" size="icon" onClick={handleSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Results table */}
      <Card>
        <CardHeader>
          <CardTitle>Forecast Details</CardTitle>
          <CardDescription>
            {data?.total ?? 0} items
            {activeBucket ? ` in "${BUCKET_CONFIG.find((b) => b.key === activeBucket)?.label}" bucket` : ''}
            {activeDueType ? ` (${DUE_TYPE_LABELS[activeDueType]})` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!data?.results?.length ? (
            <p className="text-sm text-gray-500 text-center py-8">No items in the forecast window.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 pr-4 font-medium text-gray-500">Serial</th>
                    <th className="pb-2 pr-4 font-medium text-gray-500">Manufacturer</th>
                    <th className="pb-2 pr-4 font-medium text-gray-500">Model</th>
                    <th className="pb-2 pr-4 font-medium text-gray-500">Type</th>
                    <th className="pb-2 pr-4 font-medium text-gray-500">Status</th>
                    <th className="pb-2 pr-4 font-medium text-gray-500">Due Type</th>
                    <th className="pb-2 pr-4 font-medium text-gray-500">Due Date</th>
                    <th className="pb-2 font-medium text-gray-500">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((item: GearForecastItem, idx: number) => (
                    <tr key={`${item.id}-${item.due_type}-${idx}`} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-4 font-mono text-xs">{item.serial_number}</td>
                      <td className="py-2 pr-4">{item.manufacturer}</td>
                      <td className="py-2 pr-4">{item.model}</td>
                      <td className="py-2 pr-4 capitalize">{item.type}</td>
                      <td className="py-2 pr-4 capitalize">{item.status}</td>
                      <td className="py-2 pr-4">
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          item.due_type === 'inspection' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700',
                        )}>
                          {DUE_TYPE_LABELS[item.due_type] ?? item.due_type}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{item.due_date}</td>
                      <td className={cn(
                        'py-2 font-medium',
                        item.days_until_due < 0 ? 'text-red-600' : item.days_until_due <= 30 ? 'text-orange-600' : 'text-gray-900',
                      )}>
                        {item.days_until_due < 0 ? `${Math.abs(item.days_until_due)}d overdue` : `${item.days_until_due}d`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
